import React, { useState, useEffect } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert,
  CircularProgress,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Search,
  Psychology,
  Storage,
  Analytics,
  Security,
  CheckCircle,
  HourglassEmpty,
  Timer,
} from '@mui/icons-material';

interface LoadingStage {
  name: string;
  description: string;
  icon: React.ReactElement;
  estimatedTime: number; // in seconds
  typical: string; // typical time range
}

interface EnhancedLoadingStateProps {
  useRag: boolean;
  searchType?: string;
  query: string;
}

const EnhancedLoadingState: React.FC<EnhancedLoadingStateProps> = ({
  useRag,
  searchType,
  query,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);

  // Define stages based on whether RAG is enabled
  const stages: LoadingStage[] = useRag
    ? [
        {
          name: 'Query Analysis',
          description: 'Understanding your search intent using AI',
          icon: <Psychology color="primary" />,
          estimatedTime: 5,
          typical: '3-7 seconds',
        },
        {
          name: 'Database Search',
          description: 'Searching across 15+ government databases',
          icon: <Storage color="primary" />,
          estimatedTime: 10,
          typical: '8-12 seconds',
        },
        {
          name: 'Semantic Analysis',
          description: 'Finding semantically similar contracts',
          icon: <Analytics color="primary" />,
          estimatedTime: 15,
          typical: '10-20 seconds',
        },
        {
          name: 'Result Reranking',
          description: 'Prioritizing most relevant results',
          icon: <Search color="primary" />,
          estimatedTime: 10,
          typical: '8-15 seconds',
        },
        {
          name: 'Fraud Detection',
          description: 'Analyzing patterns and risk indicators',
          icon: <Security color="primary" />,
          estimatedTime: 10,
          typical: '5-15 seconds',
        },
      ]
    : [
        {
          name: 'Database Search',
          description: 'Searching government databases',
          icon: <Storage color="primary" />,
          estimatedTime: 1,
          typical: '0.5-2 seconds',
        },
        {
          name: 'Processing Results',
          description: 'Formatting and organizing results',
          icon: <Analytics color="primary" />,
          estimatedTime: 1,
          typical: '0.5-1 second',
        },
      ];

  const totalEstimatedTime = stages.reduce((sum, stage) => sum + stage.estimatedTime, 0);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 0.1);
    }, 100);

    return () => clearInterval(timer);
  }, []);

  // Stage progression effect
  useEffect(() => {
    if (!useRag) {
      // For non-RAG searches, just show a simple progress
      setStageProgress((elapsedTime / 2) * 100);
      return;
    }

    // Calculate which stage we should be in based on elapsed time
    let accumulatedTime = 0;
    for (let i = 0; i < stages.length; i++) {
      accumulatedTime += stages[i].estimatedTime;
      if (elapsedTime < accumulatedTime) {
        setCurrentStage(i);
        const stageStart = accumulatedTime - stages[i].estimatedTime;
        const stageElapsed = elapsedTime - stageStart;
        setStageProgress((stageElapsed / stages[i].estimatedTime) * 100);
        break;
      }
    }

    // If we've exceeded estimated time, keep showing final stage
    if (elapsedTime >= totalEstimatedTime) {
      setCurrentStage(stages.length - 1);
      setStageProgress(90); // Keep at 90% to show we're still working
    }
  }, [elapsedTime, stages, totalEstimatedTime, useRag]);

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(0);
    return `${minutes}:${remainingSeconds.padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (elapsedTime < totalEstimatedTime * 0.8) return 'primary';
    if (elapsedTime < totalEstimatedTime * 1.2) return 'warning';
    return 'error';
  };

  if (!useRag) {
    // Simple loading for standard searches
    return (
      <Box sx={{ width: '100%', px: 2 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Searching for: "{query}"
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Standard keyword search - typically completes in 1-2 seconds
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.min(stageProgress, 100)} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Processing...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(elapsedTime)}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Enhanced loading for RAG searches
  return (
    <Box sx={{ width: '100%', px: 2 }}>
      <Fade in timeout={500}>
        <Paper sx={{ p: 4, mb: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">
                AI-Enhanced Search in Progress
              </Typography>
              <Chip
                icon={<Timer />}
                label={formatTime(elapsedTime)}
                color={getTimeColor()}
                variant="outlined"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Searching for: <strong>"{query}"</strong>
            </Typography>

            {/* Time estimate alert */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Expected time:</strong> {totalEstimatedTime}-{totalEstimatedTime + 10} seconds
                {elapsedTime > totalEstimatedTime && (
                  <span> (Taking longer than usual - processing complex query)</span>
                )}
              </Typography>
            </Alert>
          </Box>

          {/* Overall progress */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Overall Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.min(Math.round((elapsedTime / totalEstimatedTime) * 100), 95)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((elapsedTime / totalEstimatedTime) * 100, 95)}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Stage stepper */}
          <Stepper activeStep={currentStage} orientation="vertical">
            {stages.map((stage, index) => (
              <Step key={stage.name} completed={index < currentStage}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {index < currentStage ? (
                        <CheckCircle color="success" />
                      ) : index === currentStage ? (
                        <CircularProgress size={24} thickness={4} />
                      ) : (
                        <HourglassEmpty color="disabled" />
                      )}
                    </Box>
                  )}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {stage.name}
                    </Typography>
                    {index === currentStage && (
                      <Chip
                        label={`${Math.round(stageProgress)}%`}
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {stage.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Typical time: {stage.typical}
                  </Typography>
                  {index === currentStage && (
                    <LinearProgress
                      variant="determinate"
                      value={stageProgress}
                      sx={{ mt: 1, mb: 2 }}
                    />
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Tips while waiting */}
          <Collapse in={elapsedTime > 15}>
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Did you know?</strong> AI-enhanced search analyzes patterns across 45M+ 
                government contracts to identify potential fraud indicators and irregular spending patterns.
              </Typography>
            </Alert>
          </Collapse>

          <Collapse in={elapsedTime > 30}>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Taking longer than usual:</strong> Processing complex semantic analysis. 
                For faster results, try disabling AI enhancement or using more specific search terms.
              </Typography>
            </Alert>
          </Collapse>
        </Paper>
      </Fade>
    </Box>
  );
};

export default EnhancedLoadingState;